<script setup lang="ts">
import { useDisplay } from 'vuetify'
import { VMaskInput } from 'vuetify/labs/VMaskInput'
import { useStaffStore } from '../stores/staffStore'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import NewTreeComponent from '@/components/createdUi/NewTreeComponent.vue'
import { searchDepartments } from '@/components/createdUi/treeSearch'
import DateMaskInput from '@/components/createdUi/DateMaskInput.vue'

const props = defineProps<{
  modelValue: boolean
  departmentId?: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  created: []
}>()

const staffStore = useStaffStore()
const apiStore = useApiStore()
const { smAndDown } = useDisplay()

// Состояния диалогов
const currentStep = ref(1)
const isConfirmCancelOpen = ref(false)

// Состояния видимости оверлеев выбора
const showDepartmentSelect = ref(false)
const showPositionMenu = ref(false)

// Данные формы
const lastName = ref('')
const firstName = ref('')
const middleName = ref('')
const birthDate = ref('')
const email = ref('')
const phoneNumber = ref('')
const workPhoneNumber = ref('')

const selectedDepartmentId = ref<string | null>(null)
const selectedPositionId = ref<string | null>(null)

// Список должностей в формате дерева для FilterTreeSelect
const positionsTree = computed(() => {
  return staffStore.positionsList
    .filter(pos => pos.id !== 'empty')
    .map(pos => ({
      key: pos.id,
      label: pos.name || 'Не указано',
      data: {
        name: pos.name || 'Не указано',
        selfId: pos.id,
      },
      children: [],
    }))
})

// Обработчики выбора
function onDepartmentSelect(value: { id: string } | null) {
  selectedDepartmentId.value = value ? value.id : null
}

// Обработчик выбора должности
function onPositionSelect(value: { id: string } | null) {
  selectedPositionId.value = value ? value.id : null
  showPositionMenu.value = false
}

// Наблюдение за открытием диалога для сброса состояния
watch(() => props.modelValue, async isOpen => {
  if (isOpen) {
    currentStep.value = 1
    lastName.value = ''
    firstName.value = ''
    middleName.value = ''
    birthDate.value = ''
    email.value = ''
    phoneNumber.value = ''
    workPhoneNumber.value = ''
    selectedDepartmentId.value = props.departmentId ? String(props.departmentId) : null
    selectedPositionId.value = null

    if (!apiStore.departmentsTree || !apiStore.departmentsTree.length) {
      await apiStore.fetchAllDepartments()
    }

    if (!staffStore.positionsList || !staffStore.positionsList.length) {
      await staffStore.fetchPositions()
    }
  }
})

// Валидация по шагам
const isStep1Valid = computed(() => {
  return (
    lastName.value.trim() !== ''
    && firstName.value.trim() !== ''
    && phoneNumber.value.trim() !== ''
  )
})

const isStep2Valid = computed(() => {
  return (
    selectedDepartmentId.value !== null
    && selectedPositionId.value !== null
  )
})

// Навигация по шагам
function nextStep() {
  if (currentStep.value === 1 && isStep1Valid.value) {
    currentStep.value = 2
  }
}

// Навигация назад
function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

// Попытка закрыть по крестику
function handleCloseClick() {
  const isFormEmpty
    = lastName.value.trim() === ''
    && firstName.value.trim() === ''
    && middleName.value.trim() === ''
    && birthDate.value === ''
    && email.value.trim() === ''
    && phoneNumber.value.trim() === ''

  if (currentStep.value === 1 && isFormEmpty) {
    emit('update:modelValue', false)
  } else {
    isConfirmCancelOpen.value = true
  }
}

// Подтверждение выхода
function confirmExit() {
  isConfirmCancelOpen.value = false
  emit('update:modelValue', false)
}

// Отмена выхода
function cancelExit() {
  isConfirmCancelOpen.value = false
}

// Финальное добавление сотрудника
async function submitForm() {
  if (!selectedDepartmentId.value || !selectedPositionId.value) return

  try {
    await staffStore.addEmployee({
      last_name: lastName.value.trim(),
      first_name: firstName.value.trim(),
      middle_name: middleName.value.trim(),
      birth_date: birthDate.value || null,
      email: email.value.trim(),
      phone_number: phoneNumber.value.trim(),
      work_phone_number: workPhoneNumber.value.trim() || null,
      department: { id: selectedDepartmentId.value },
      position: { id: selectedPositionId.value },
    })
    emit('created')
    emit('update:modelValue', false)
  } catch (error) {}
}
</script>

<template>
  <!-- Основной диалог добавления сотрудника -->
  <VDialog
    :model-value="modelValue"
    :max-width="smAndDown ? '100%' : 580"
    :fullscreen="smAndDown"
    persistent
    no-click-animation
    class="add-employee-dialog-wrapper"
  >
    <VCard
      class="d-flex flex-column"
      style="width: 100% !important; max-width: 580px !important; border: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important; position: relative; overflow: visible; background-color: rgb(var(--v-theme-surface)) !important;"
      :style="smAndDown ? 'padding: 20px 20px 24px 20px !important; border-radius: 0px !important; border: none !important; min-height: 100vh;' : 'padding: 24px 32px 32px 32px !important; border-radius: 12px !important;'"
    >
      <!-- Крестик закрытия -->
      <VBtn
        icon="ri-close-line"
        variant="text"
        density="comfortable"
        class="close-dialog-btn"
        style="position: absolute; z-index: 10; width: 32px !important; height: 32px !important; min-width: 32px !important; padding: 0 !important;"
        :style="smAndDown ? 'top: 16px; right: 20px;' : 'top: 24px; right: 32px;'"
        @click="handleCloseClick"
      />

      <VCardText class="pa-0">
        <!-- Заголовок диалога -->
        <h2
          style="font-family: 'Inter', sans-serif !important; font-size: 24px !important; font-weight: 500 !important; color: rgba(var(--v-theme-on-surface), 0.9) !important; line-height: 32px !important; margin: 0 0 4px !important;"
          :style="smAndDown ? 'padding-inline-end: 56px !important;' : 'padding-inline-end: 72px !important;'"
        >
          Добавление сотрудника
        </h2>

        <!-- Подзаголовок с текущим шагом -->
        <div style="font-family: 'Inter', sans-serif !important; font-size: 15px !important; font-weight: 400 !important; color: rgba(var(--v-theme-on-surface), 0.55) !important; line-height: 22px !important; margin-bottom: 24px !important;">
          <template v-if="currentStep === 1">
            Шаг 1 из 2 — Персональная информация
          </template>
          <template v-else-if="currentStep === 2">
            Шаг 2 из 2 — Должность и структура
          </template>
        </div>

        <div class="step-content-container">
          <!-- Шаг 1: Персональная информация -->
          <div v-if="currentStep === 1" class="d-flex flex-column gap-5">
            <VTextField
              v-model="lastName"
              variant="outlined"
              hide-details
              class="custom-field"
              persistent-placeholder
            >
              <template #label>
                <span>Фамилия<sup class="required-label">*</sup></span>
              </template>
            </VTextField>
            <VTextField
              v-model="firstName"
              variant="outlined"
              hide-details
              class="custom-field"
              persistent-placeholder
            >
              <template #label>
                <span>Имя<sup class="required-label">*</sup></span>
              </template>
            </VTextField>
            <VTextField
              v-model="middleName"
              label="Отчество"
              variant="outlined"
              hide-details
              class="custom-field"
              persistent-placeholder
            />
            <DateMaskInput
              v-model="birthDate"
              class="custom-field-date"
            >
              <template #label>
                <span>Дата рождения</span>
              </template>
            </DateMaskInput>
            <div
              class="d-flex gap-5"
              :class="smAndDown ? 'flex-column' : ''"
            >
              <VTextField
                v-model="email"
                label="Электронная почта"
                variant="outlined"
                density="default"
                hide-details
                class="custom-field flex-grow-1"
                :class="smAndDown ? 'w-100' : 'w-50'"
                persistent-placeholder
              />
              <VMaskInput
                v-model="phoneNumber"
                mask="+7 (###) ###-##-##"
                variant="outlined"
                hide-details
                class="custom-field flex-grow-1"
                :class="smAndDown ? 'w-100' : 'w-50'"
                persistent-placeholder
              >
                <template #label>
                  <span>Личный телефон<sup class="required-label">*</sup></span>
                </template>
              </VMaskInput>
            </div>

            <div
              class="d-flex gap-5"
              :class="smAndDown ? 'flex-column' : ''"
            >
              <VTextField
                v-model="workPhoneNumber"
                label="Служебный телефон"
                variant="outlined"
                density="default"
                hide-details
                class="custom-field flex-grow-1 w-100"
                persistent-placeholder
              />
            </div>

            <!-- Кнопка Далее -->
            <div
              class="d-flex pt-3"
              :class="smAndDown ? 'justify-center w-100' : 'justify-end'"
            >
              <VBtn
                color="primary"
                class="action-btn next-btn"
                :class="{ 'w-100': smAndDown }"
                :disabled="!isStep1Valid"
                @click="nextStep"
              >
                Далее <VIcon icon="ri-arrow-right-line" class="ms-1" size="18" />
              </VBtn>
            </div>
          </div>

          <!-- Шаг 2: Должность и структура -->
          <div v-if="currentStep === 2" class="d-flex flex-column gap-5">
            <!-- Выбор Подразделения и Должности -->
            <NewTreeComponent
              v-model:visible="showDepartmentSelect"
              placeholder="Выберите подразделение"
              label="Подразделение"
              is-label-required
              search-placeholder="Поиск по подразделениям"
              :selected="{ value: selectedDepartmentId ? { id: selectedDepartmentId } : null }"
              :tree-data="apiStore.departmentsTree"
              :search-tree="searchDepartments"
              @update:selected="onDepartmentSelect"
            />

            <NewTreeComponent
              v-model:visible="showPositionMenu"
              placeholder="Выберите должность"
              label="Должность"
              is-label-required
              search-placeholder="Поиск по должностям"
              :selected="{ value: selectedPositionId ? { id: selectedPositionId } : null }"
              :tree-data="positionsTree"
              @update:selected="onPositionSelect"
            />

            <!-- Кнопки Назад и Далее -->
            <div
              class="d-flex justify-space-between pt-3"
              :class="smAndDown ? 'w-100 gap-3' : ''"
            >
              <VBtn
                variant="outlined"
                class="action-btn back-btn"
                :class="{ 'w-50': smAndDown }"
                @click="prevStep"
              >
                <VIcon icon="ri-arrow-left-line" class="me-1" size="18" /> Назад
              </VBtn>
              <VBtn
                color="primary"
                class="action-btn next-btn add-submit-btn"
                :class="{ 'w-50': smAndDown }"
                :disabled="!isStep2Valid"
                @click="submitForm"
              >
                Добавить
              </VBtn>
            </div>
          </div>
        </div>
      </VCardText>
    </VCard>
  </VDialog>

  <!-- Диалог подтверждения закрытия -->
  <VDialog
    v-model="isConfirmCancelOpen"
    max-width="560"
    persistent
    no-click-animation
    class="confirm-exit-dialog"
    content-class="entity-form-menu"
  >
    <VCard class="d-flex flex-column bg-surface" style="padding: 0 !important; border-radius: 10px !important; border: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important; width: 560px !important; height: 166px !important;">
      <!-- Шапка -->
      <div style="padding: 20px !important; height: 68px !important;">
        <h3 style="font-family: 'Inter', sans-serif !important; font-size: 18px !important; font-weight: 500 !important; color: rgba(var(--v-theme-on-surface), 0.9) !important; line-height: 28px !important; margin: 0;">
          Подтверждение
        </h3>
      </div>

      <!-- Тело -->
      <div style="padding: 0 20px 20px !important; height: 40px !important;">
        <p style="font-family: 'Inter', sans-serif !important; font-size: 15px !important; font-weight: 400 !important; color: rgba(var(--v-theme-on-surface), 0.7) !important; line-height: 20px !important; margin: 0;">
          Данные не сохранятся. Вы действительно хотите выйти?
        </p>
      </div>

      <!-- Подвал -->
      <div style="padding: 0 20px 20px !important; height: 58px !important;">
        <div class="d-flex justify-end gap-4">
          <VBtn
            variant="outlined"
            class="action-btn back-btn confirm-btn-cancel"
            @click="cancelExit"
          >
            Отменить
          </VBtn>
          <VBtn
            color="primary"
            class="action-btn next-btn confirm-btn-exit"
            @click="confirmExit"
          >
            Выйти
          </VBtn>
        </div>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.close-dialog-btn {
  color: rgba(var(--v-theme-on-surface), 0.45) !important;

  &:hover {
    color: rgba(var(--v-theme-on-surface), 0.9) !important;
  }
}

.step-content-container {
  display: flex;
  flex-direction: column;
}

.custom-field,
.custom-field-date {
  :deep(.v-field__control) {
    border-radius: 6px !important;
    height: 48px !important;
    background-color: transparent !important;
    border-color: rgba(var(--v-theme-on-surface), 0.12) !important;

    &.v-field--focused {
      border-color: rgb(var(--v-theme-primary)) !important;
    }
  }

  :deep(.v-field__input) {
    padding-inline: 16px !important;
    font-family: 'Inter', sans-serif !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    height: 48px !important;
    display: flex;
    align-items: center;
    color: rgba(var(--v-theme-on-surface), 0.9) !important;

    &::placeholder {
      color: rgba(var(--v-theme-on-surface), 0.38) !important;
      opacity: 1;
    }
  }
}

.custom-select {
  :deep(.v-field__append-inner) {
    align-self: center !important;
    padding-top: 0 !important;
  }
}

.action-btn {
  font-family: 'Inter', sans-serif !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  height: 38px !important;
  border-radius: 6px !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  padding: 8px 18px !important;
}

.next-btn {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: #ffffff !important;
  min-width: 88px !important;
}

.add-submit-btn {
  min-width: 108px !important;
}

.back-btn {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  color: rgba(var(--v-theme-on-surface), 0.55) !important;
  background-color: transparent !important;
  min-width: 102px !important;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.04) !important;
  }
}

:global(.edit-employee-select-menu) {
  max-width: 0 !important;
}
</style>
